import { useQuery } from 'react-query';
import { useApiGet, useApiQuery } from '../../../api';
import { useBuildVars } from '../../../config';
import { useCopCountries } from 'src/hooks/copCountries';

export interface ProgramDetailLoadRequest {
  countryCode: string;
  vendorID: number;
  vendorprogramid?: number;
}

export interface ManualTemplates {
  source: string;
}

export type ProgramDetailFieldProperties = {
  accountNumber: string;
  acopGracePeriod?: string;
  acopTypeOverride: true;
  addAdditionalCost: boolean;
  autoQuote: string;
  averageCostValidation: string;
  bcnLinkingLevel: string;
  bidRequired: string;
  bidType: string;
  claimAccountNumberKey: string;
  claimName: string;
  coG_Approval: string;
  companyCode: string;
  completeShipment?: boolean;
  costAdjustmentType: string;
  costBasis: string;
  costBasisOverride: string;
  currencyGlobal: string;
  discountType: string;
  discountVisible: string;
  enableCustomOnDemandQuote: string;
  enableExpiryDateAttributeChange?: boolean;
  enableProductFamilyDiscounts: boolean;
  enableQuoteQuantityEdit: string;
  enableRulesEngine: boolean;
  enableSequencing: boolean;
  euMandatoryForClaims: string;
  fundUsageAmount: string;
  glAccount: string;
  impulseAcop: string;
  impulseCommentLine1: string;
  impulseCommentLine2: string;
  impulseCompetitor: string;
  impulseComponentFlag: string;
  impulseDebitEnOPercentage: string;
  impulseDebitMDFPercentage: string;
  impulseDebitOTHPercentage: string;
  impulseDebitVRFPPercentage: string;
  impulseDebitVRFVPercentage: string;
  impulseDebitVendorPercentage: string;
  impulseFaxFlag: string;
  impulseHardCopyFlag: string;
  impulseMinimumAcopType: string;
  impulsePROMOFLAG: string;
  impulsePrintCommentsFlag: string;
  impulseProgramName: string;
  impulseReverseAcop: string;
  ingramCostBasis: string;
  isMapAllIMSku: boolean;
  isMultiReseller: string;
  isPartialOrderingAllowed: boolean;
  isProgramEligibleForFreeProducts: boolean;
  isSplitAndRemoveAllowed: boolean;
  msrpSource: string;
  nonorderlevel: string;
  orderReCostRePrice: string;
  orderType: string;
  poccFlagging: string;
  productIncExc: string;
  productLocalizationForWeb: string;
  quantityTracking: string;
  resellerAuthorization: string;
  selectableDiscountLogic: string;
  sharedQuantityflag: string;
  suggestedResellerPriceRequired: boolean;
  thresholdCount: string;
  uniqueEnduserResellerForClaims: boolean;
  vendorACOPDiscountType: string;
  vendorDiscountHold: string;
  visibleonWeb: string;
};

export type VendorProgramDetail = {
  businessType: string;
  createdby: string;
  createddate: string;
  customergroupinfo: [];
  endUserAddressMatchPercentage: string;
  isExactEnduserNameMatchRequired?: string;
  lastchange_user: string;
  lastchanged_date: string;
  sk_company: number;
  status: '0' | '1' | '2';
  uniqueEnduserResellerForClaims: boolean;
  vendorcode: string;
  vendorid: number;
  vendorname: string;
  vendorprogramdescription: string;
  vendorprogramid: number;
  vendorprogramsdescription: string;
  vendorprogramsname: string;
};

export type ProgramDetailLoadResponse = {
  manualTemplates?: ManualTemplates;
  manualTemplatesFieldProperties: ProgramDetailFieldProperties;
  vendorProgramDetail?: VendorProgramDetail;
};

export function useProgramDetailLoad(
  params: ProgramDetailLoadRequest,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  return useApiQuery<ProgramDetailLoadResponse>(
    '/system.webapi.gds/api/v1/gdsCoreApiVendor/vendorProgramEdit',
    params,
    {
      enabled:
        enabled &&
        !Number.isNaN(params.vendorID) &&
        !Number.isNaN(params.vendorprogramid),
    },
    undefined,
    { host: buildVars.VP_API_BASE }
  );
}

export type AccountNumbersList =
  | {
      account_name: string;
      account_nbr: string;
      sk_account: string;
    }[]
  | null;

export function useGetAccountNumbersList() {
  const buildVars = useBuildVars();
  const { isCopCountries } = useCopCountries();
  const apiGet = useApiGet<AccountNumbersList>({
    host: buildVars.VP_API_BASE,
  });
  return useQuery(
    ['account-numbers', {}, {}],
    () =>
      apiGet(
        '/system.webapi.gds/api/v1/GdsCoreApiBidClaimAccountNumbers/bidclaims/accountnumbers'
      ),
    { enabled: isCopCountries() }
  );
}
